import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Inline field controls for the visual strategy editor (2.8).
 *
 * Every control is display-first: it renders the draft value and hands the
 * changed value upward on commit (blur / Enter / explicit action). Nothing
 * here talks to the server — the page collects edits into patch ops and one
 * Save applies them all.
 */

export const INPUT_CLS =
  "w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm " +
  "focus:outline-none focus:ring-1 focus:ring-blue-400";

export const SMALL_BUTTON_CLS =
  "rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-50";

export function FieldRow({
  label,
  edited,
  hint,
  children,
}: {
  label: string;
  edited?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-0.5 flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
        <span>{label}</span>
        {edited ? (
          <span
            title="unsaved change"
            className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
          />
        ) : null}
      </div>
      {children}
      {hint ? <div className="mt-0.5 text-[11px] text-[var(--fg-muted)]">{hint}</div> : null}
    </div>
  );
}

/** Text input committing on blur or Enter. Empty commits as null (remove). */
export function TextField({
  value,
  onCommit,
  placeholder,
  multiline,
  disabled,
  ariaLabel,
}: {
  value: string | null | undefined;
  onCommit: (value: string | null) => void;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const saved = value ?? "";
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);

  const commit = () => {
    const next = draft.trim() === "" ? null : draft;
    if ((next ?? "") !== saved) onCommit(next);
  };

  if (multiline) {
    return (
      <textarea
        aria-label={ariaLabel}
        className={cn(INPUT_CLS, "min-h-[4.5rem] font-normal")}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
      />
    );
  }
  return (
    <input
      aria-label={ariaLabel}
      className={INPUT_CLS}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setDraft(saved);
      }}
    />
  );
}

/** Number input; empty commits as null (remove the key). */
export function NumberField({
  value,
  onCommit,
  min,
  max,
  disabled,
  ariaLabel,
}: {
  value: number | null | undefined;
  onCommit: (value: number | null) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const saved = value === null || value === undefined ? "" : String(value);
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);

  const commit = () => {
    if (draft.trim() === "") {
      if (saved !== "") onCommit(null);
      return;
    }
    const n = Number(draft);
    if (!Number.isFinite(n)) {
      setDraft(saved);
      return;
    }
    if (String(n) !== saved) onCommit(n);
  };

  return (
    <input
      aria-label={ariaLabel}
      type="number"
      className={cn(INPUT_CLS, "tabular-nums")}
      value={draft}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setDraft(saved);
      }}
    />
  );
}

export function BoolField({
  value,
  onCommit,
  label,
  disabled,
}: {
  value: boolean | null | undefined;
  onCommit: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={value === true}
        disabled={disabled}
        onChange={(e) => onCommit(e.target.checked)}
        className="h-4 w-4 rounded border-[var(--border)]"
      />
      {label}
    </label>
  );
}

export function SelectField({
  value,
  onCommit,
  options,
  allowEmpty,
  disabled,
  ariaLabel,
}: {
  value: string | null | undefined;
  onCommit: (value: string | null) => void;
  options: { value: string; label: string }[];
  allowEmpty?: string;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <select
      aria-label={ariaLabel}
      className={INPUT_CLS}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onCommit(e.target.value === "" ? null : e.target.value)}
    >
      {allowEmpty !== undefined ? <option value="">{allowEmpty}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * A list of short strings as removable chips plus an adder. Commits the whole
 * list on every change; an empty list commits as null so the key disappears
 * from the YAML rather than lingering as `[]`.
 */
export function ChipListField({
  value,
  onCommit,
  addLabel,
  disabled,
}: {
  value: string[] | null | undefined;
  onCommit: (value: string[] | null) => void;
  addLabel: string;
  disabled?: boolean;
}) {
  const items = value ?? [];
  const [draft, setDraft] = useState("");

  const commit = (next: string[]) => onCommit(next.length === 0 ? null : next);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1">
        {items.map((item, i) => (
          <span
            key={`${item}-${i}`}
            className="flex items-center gap-1 rounded border border-[var(--border)] px-1.5 py-0.5 text-[11px]"
          >
            {item}
            {!disabled ? (
              <button
                type="button"
                aria-label={`Remove ${item}`}
                className="text-[var(--fg-muted)] hover:text-[var(--danger)]"
                onClick={() => commit(items.filter((_, j) => j !== i))}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {items.length === 0 ? (
          <span className="text-xs text-[var(--fg-muted)]">none</span>
        ) : null}
      </div>
      {!disabled ? (
        <form
          className="mt-1 flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const v = draft.trim();
            if (!v || items.includes(v)) return;
            commit([...items, v]);
            setDraft("");
          }}
        >
          <input
            aria-label={addLabel}
            className={cn(INPUT_CLS, "max-w-[14rem] text-xs")}
            value={draft}
            placeholder={addLabel}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            type="submit"
            disabled={!draft.trim()}
            className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-50"
          >
            Add
          </button>
        </form>
      ) : null}
    </div>
  );
}

/**
 * A list of sentence-length strings (safeguards, AI rules) as editable rows.
 * Rows commit on blur; removing the last row commits null so the key is
 * dropped from the YAML.
 */
export function TextListField({
  value,
  onCommit,
  addLabel,
  disabled,
}: {
  value: string[] | null | undefined;
  onCommit: (value: string[] | null) => void;
  addLabel: string;
  disabled?: boolean;
}) {
  const items = value ?? [];
  const [adding, setAdding] = useState("");

  const commit = (next: string[]) => {
    const cleaned = next.map((s) => s.trim()).filter((s) => s !== "");
    onCommit(cleaned.length === 0 ? null : cleaned);
  };

  return (
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-1">
          <RowInput
            value={item}
            disabled={disabled}
            onCommitRow={(v) => {
              const next = [...items];
              if (v.trim() === "") next.splice(i, 1);
              else next[i] = v;
              commit(next);
            }}
          />
          {!disabled ? (
            <button
              type="button"
              aria-label={`Remove rule ${i + 1}`}
              className="mt-1 shrink-0 text-sm text-[var(--fg-muted)] hover:text-[var(--danger)]"
              onClick={() => commit(items.filter((_, j) => j !== i))}
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
      {items.length === 0 ? (
        <p className="text-xs text-[var(--fg-muted)]">none configured</p>
      ) : null}
      {!disabled ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const v = adding.trim();
            if (!v) return;
            commit([...items, v]);
            setAdding("");
          }}
        >
          <input
            aria-label={addLabel}
            className={cn(INPUT_CLS, "text-xs")}
            value={adding}
            placeholder={addLabel}
            onChange={(e) => setAdding(e.target.value)}
          />
          <button
            type="submit"
            disabled={!adding.trim()}
            className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-50"
          >
            Add
          </button>
        </form>
      ) : null}
    </div>
  );
}

function RowInput({
  value,
  disabled,
  onCommitRow,
}: {
  value: string;
  disabled?: boolean;
  onCommitRow: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <input
      className={cn(INPUT_CLS, "text-xs")}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value) onCommitRow(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setDraft(value);
      }}
    />
  );
}

/**
 * A list of structured rows (rules, questions, computed fields). Owns nothing
 * but the layout: the caller renders each row's controls and receives the
 * whole list back on every change, so one op carries the list. An empty list
 * commits as null and the key leaves the YAML.
 */
export function RowListField<T>({
  value,
  onCommit,
  renderRow,
  newRow,
  addLabel,
  rowLabel,
  emptyText = "none configured",
  disabled,
}: {
  value: T[] | null | undefined;
  onCommit: (value: T[] | null) => void;
  /** Render one row's controls; call `update` with the replacement row. */
  renderRow: (row: T, update: (next: T) => void, index: number) => React.ReactNode;
  newRow: () => T;
  addLabel: string;
  /** Accessible name for a row's remove/move controls, e.g. "question". */
  rowLabel: string;
  emptyText?: string;
  disabled?: boolean;
}) {
  const rows = value ?? [];
  const commit = (next: T[]) => onCommit(next.length === 0 ? null : next);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row as T);
    commit(next);
  };

  return (
    <div className="space-y-2">
      {rows.length === 0 ? (
        <p className="text-xs text-[var(--fg-muted)]">{emptyText}</p>
      ) : null}
      {rows.map((row, i) => (
        <div
          key={i}
          className="flex items-start gap-1 rounded border border-[var(--border)] p-2 text-xs"
        >
          <div className="min-w-0 flex-1 space-y-1.5">
            {renderRow(row, (next) => commit(rows.map((r, j) => (j === i ? next : r))), i)}
          </div>
          {!disabled ? (
            <div className="flex shrink-0 flex-col items-center gap-0.5 text-[var(--fg-muted)]">
              <button
                type="button"
                aria-label={`Move ${rowLabel} ${i + 1} up`}
                disabled={i === 0}
                className="leading-none hover:text-[var(--fg)] disabled:opacity-30"
                onClick={() => move(i, i - 1)}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move ${rowLabel} ${i + 1} down`}
                disabled={i === rows.length - 1}
                className="leading-none hover:text-[var(--fg)] disabled:opacity-30"
                onClick={() => move(i, i + 1)}
              >
                ↓
              </button>
              <button
                type="button"
                aria-label={`Remove ${rowLabel} ${i + 1}`}
                className="leading-none hover:text-[var(--danger)]"
                onClick={() => commit(rows.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ) : null}
        </div>
      ))}
      {!disabled ? (
        <button
          type="button"
          className={SMALL_BUTTON_CLS}
          onClick={() => commit([...rows, newRow()])}
        >
          {addLabel}
        </button>
      ) : null}
    </div>
  );
}

/** A labelled control inside a RowListField row. */
export function RowField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="text-[var(--fg-muted)]">{label}</span>
      {children}
    </label>
  );
}

/**
 * Inline text input for a row: local draft, commits on blur or Enter so a
 * keystroke does not rewrite the whole list. Commits the raw string; the
 * caller decides what an empty value means.
 */
export function RowTextInput({
  value,
  onCommit,
  ariaLabel,
  placeholder,
  mono,
  multiline,
  disabled,
}: {
  value: string | null | undefined;
  onCommit: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  mono?: boolean;
  multiline?: boolean;
  disabled?: boolean;
}) {
  const saved = value ?? "";
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);
  const cls = cn(INPUT_CLS, "text-xs", mono && "font-mono");
  if (multiline) {
    return (
      <textarea
        aria-label={ariaLabel}
        className={cn(cls, "min-h-[4rem]")}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={!mono}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== saved) onCommit(draft);
        }}
      />
    );
  }
  return (
    <input
      aria-label={ariaLabel}
      className={cls}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      spellCheck={!mono}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== saved) onCommit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setDraft(saved);
      }}
    />
  );
}
